import React, { useEffect } from 'react';
import { MapPin, Check } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { coverageAreas } from '../utils/mockData';
import ServiceAreaMap from '../components/ServiceAreaMap';

const Coverage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Header */}
      <section className="bg-gradient-to-r from-cyan-600 to-emerald-600 py-10">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Areas We Cover
          </h1>
          <p className="text-xl text-cyan-50 max-w-2xl mx-auto">
            Proudly serving Neath and surrounding areas within an approx 20-mile radius.
          </p>
        </div>
      </section>

      {/* Main Coverage Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Interactive Map */}
            <div className="mb-12">
              <ServiceAreaMap />
            </div>

            {/* Not in Area Section - Moved here */}
            <div className="mb-12">
              <div className="max-w-4xl mx-auto text-center">
                <Card className="border-2 border-gray-200">
                  <CardContent className="py-8 px-12">
                    <MapPin className="w-16 h-16 text-cyan-600 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      Not Sure If We Cover Your Area?
                    </h2>
                    <p className="text-lg text-gray-600 mb-6">
                      We're always looking to expand our service area. Get in touch and we'll let you know if we can help.
                    </p>
                    <a
                      href="/contact"
                      className="inline-block bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
                    >
                      Contact Us Today
                    </a>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Coverage Areas */}
            <div className="grid md:grid-cols-2 gap-8">
              {/* Areas List */}
              <Card className="border-2 border-gray-200">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Areas We Cover</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {coverageAreas.map((area, index) => (
                      <div key={index} className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg hover:bg-cyan-50 transition-colors">
                        <div className="flex-shrink-0 w-5 h-5 bg-gradient-to-br from-cyan-500 to-emerald-500 rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                        <span className="text-gray-800 font-medium text-sm">{area}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-gray-500 mt-6 pt-6 border-t">
                    Don't see your area listed? Contact us to check if we can serve your location.
                  </p>
                </CardContent>
              </Card>

              {/* Service Info */}
              <div className="space-y-6">
                <Card className="border-2 border-cyan-200 bg-gradient-to-br from-cyan-50 to-emerald-50">
                  <CardContent className="p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Why Local Matters</h3>
                    <div className="space-y-4">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center mt-0.5">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">Quick Response Times</p>
                          <p className="text-sm text-gray-600">We're nearby and can reach you fast</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center mt-0.5">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">Local Knowledge</p>
                          <p className="text-sm text-gray-600">We know the area and understand local needs</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center mt-0.5">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">Community Focused</p>
                          <p className="text-sm text-gray-600">Supporting local businesses and residents</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center mt-0.5">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">Flexible Scheduling</p>
                          <p className="text-sm text-gray-600">Easy to arrange convenient appointment times</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-gray-200">
                  <CardContent className="p-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Service Statistics</h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center pb-2 border-b">
                        <span className="text-gray-600">Average Response Time</span>
                        <span className="font-bold text-cyan-600">Under 24 hrs</span>
                      </div>
                      <div className="flex justify-between items-center pb-2 border-b">
                        <span className="text-gray-600">Service Radius</span>
                        <span className="font-bold text-cyan-600">Approx 20 Miles</span>
                      </div>
                      <div className="flex justify-between items-center pb-2 border-b">
                        <span className="text-gray-600">Towns Covered</span>
                        <span className="font-bold text-cyan-600">35+</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Customer Satisfaction</span>
                        <span className="font-bold text-cyan-600">99.9%</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-16 bg-gradient-to-br from-cyan-50 to-emerald-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              <Card className="border-2 border-gray-200">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Do you charge extra for travel?</h3>
                  <p className="text-gray-600">
                    No, our prices include travel within our approx 20-mile service radius from Neath. There are no hidden fees.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-2 border-gray-200">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Do I have to have my windows cleaned regularly?</h3>
                  <p className="text-gray-600">
                    No, we're happy to provide one-off cleans. However, these are priced slightly higher than regular maintenance cleans as windows and frames typically require more time and effort when they haven't been cleaned recently.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-2 border-gray-200">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Can you service areas outside the approx 20-mile radius?</h3>
                  <p className="text-gray-600">
                    It depends on the location and size of the job. Please contact us to discuss your specific requirements.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-2 border-gray-200">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">I've only got 3 or 4 windows to clean, how much will that cost?</h3>
                  <p className="text-gray-600">
                    We have a minimum charge of £12 for regular maintenance cleans and £15 for first or one-off cleans. This minimum charge covers our time, equipment setup, travel, and ensures every job is completed to our professional standards.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-2 border-gray-200">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">How do I book a service?</h3>
                  <p className="text-gray-600">
                    Simply contact us via phone, email, or our contact form. We'll arrange a convenient time for your service.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Coverage;
